import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Col from './Col';
import {
    GRID_CHILD_ERROR,
    MINIMUM_COL_AMOUNT_ERROR,
    THEMES,
    SIZES,
    CENTER
} from './constants';
import classes from './index.module.scss';

/**
 * Generates a single col, enriched with props.
 * @param  {React.Component} component A Col.
 * @param  {Number} index The col's index.
 * @return {React.Component} The enriched Col.
 */
const generateCol = (component, index) => {
    if (['boolean', 'undefined'].includes(typeof component) || component === null) {
        return null;
    }

    if (component.type !== Col) {
        throw new Error(GRID_CHILD_ERROR);
    }

    return <Col {...component.props} key={index}/>;
};

/**
 * @deprecated Please use Grid from @fiverr-private/layout_components instead.
 */
const Grid = ({
    children,
    className: customClassName,
    theme,
    center
}) => {

    if (!children) {
        throw new Error(MINIMUM_COL_AMOUNT_ERROR);
    }

    const cols = Array.isArray(children) ? children.map((component, index) => generateCol(component, index)) : [generateCol(children, 0)];

    let centeringClasses = [];
    if (Array.isArray(center)) {
        centeringClasses = center
            .filter((size) => Object.values(SIZES).includes(size))
            .map((size) => `${CENTER}-${size}`);
    }

    const className = classnames(
        classes.grid,
        classes[`theme-${theme}`],
        customClassName,
        centeringClasses
    );

    return (
        <div className={className}>
            {cols.map((col) => {
                if (!col) { return null; }
                return col;
            })}
        </div>
    );
};

Grid.displayName = 'Grid';

Grid.propTypes = {
    /**
     * The displayed cols (see notes).
     */
    children: PropTypes.arrayOf(Col).isRequired,
    /**
     * The component's theme. <br>Supported options are exposed through the static `THEMES` property.
     */
    theme: PropTypes.oneOf(Object.values(THEMES)),
    /**
     * Custom classes to pass to the component.
     */
    className: PropTypes.string,
    /**
     * Array of sizes where the grid will be centered.
     */
    center: PropTypes.arrayOf(PropTypes.oneOf(Object.values(SIZES)))
};

Grid.defaultProps = {
    theme: THEMES.TWELVE,
    center: []
};

Object.assign(Grid, {
    THEMES,
    SIZES,
    Col
});

export {
    Col
};

export default Grid;
